import type { MaybeRefOrGetter, WatchSource } from 'vue'

type TRevenueEmployeeRanges = {
  yearlyRevenueRanges: TFormSelectionListItem[]
  employeeCountRanges: TFormSelectionListItem[]
}

/**
 * Client-side data access for the lead-application wizard.
 * Centralizes the `/api` route strings so components don't hardcode them.
 */
export const useLeadApplicationRepository = () => {
  const { locale, localeIsoCode } = useLocaleIso()

  /**
   * Builder.io-backed option list for a single wizard step, keyed by the
   * step's `builderModelEntryName`. Reactive: refetches when the entry name,
   * locale, or any extra watch source changes.
   */
  const getFormSelectionList = (
    entryName: MaybeRefOrGetter<string>,
    extraWatchSources: WatchSource[] = []
  ) =>
    useFetch<TFormSelectionListItem[]>(
      () => `/api/form-selection-list/${localeIsoCode.value}`,
      {
        method: 'POST',
        body: computed(() => ({ entryName: toValue(entryName) })),
        query: computed(() => ({ entryName: toValue(entryName) })),
        default: () => [],
        watch: [locale, ...extraWatchSources],
        lazy: true,
        server: false,
      }
    )

  /**
   * Combined yearly-revenue + employee-count range option lists used by the
   * review step. Preserves the original per-locale async-data cache key.
   */
  const getRevenueEmployeeRanges = () =>
    useAsyncData<TRevenueEmployeeRanges>(
      () =>
        `/api/${localeIsoCode.value}-base-yearly-revenue-employee-count-ranges`,
      async () => {
        const [yearlyRevenueRanges, employeeCountRanges] = await Promise.all([
          $fetch<TFormSelectionListItem[]>(
            `/api/form-selection-list/${localeIsoCode.value}`,
            {
              query: { entryName: 'LeadApplication:Base:YearlyRevenueRanges' },
              method: 'POST',
            }
          ),
          $fetch<TFormSelectionListItem[]>(
            `/api/form-selection-list/${localeIsoCode.value}`,
            {
              query: { entryName: 'LeadApplication:Base:EmployeeCountRanges' },
              method: 'POST',
            }
          ),
        ])

        return { yearlyRevenueRanges, employeeCountRanges }
      },
      {
        default: () => ({
          yearlyRevenueRanges: [],
          employeeCountRanges: [],
        }),
        watch: [locale],
      }
    )

  // TODO(wiring): implement the wizard submit via the injected client fetcher.
  // Endpoint + payload contract pending. Once known:
  //   1. Add `mapWizardDataToSubmitPayload(data)` in ../utils/ mapping
  //      ILeadApplicationWizardData -> LeadApplicationSubmitPayload.
  //   2. Add the `POST /api/v1/leads/application` server route (validates the
  //      body, calls createLeadApplicationRepository(event).submitApplication).
  //   3. Add here, using the `/api` client fetcher:
  //        const api = useApi()
  //        const submitApplication = (data: ILeadApplicationWizardData) =>
  //          api<LeadApplicationSubmitResponse>('/v1/leads/application', {
  //            method: 'POST',
  //            body: mapWizardDataToSubmitPayload(data),
  //          })
  //   4. Return it, and call it from the wizard's last step via
  //      useRepositories().leadApplication.submitApplication(...)
  //      (see the TODO in ../components/LeadApplication/Wizard/index.vue).

  return { getFormSelectionList, getRevenueEmployeeRanges }
}
